/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/

import { BaseService } from './base';
import { DeviceDTO } from './device';
import { AssetTelemetry, LatestTelemetryService } from './latest';

/**
 * Defines the states an asset could be in.
 */
export enum MotionState {
    OFF = 'OFF',
    MOVING = 'MOVING',
    STOPPED = 'STOPPED',
    IDLE = 'IDLE',
}

export enum InputType {
    SPREADER = 'SPREADER',
    PLOW = 'PLOW',
    MOLDBOARD = 'MOLDBOARD',
}

export interface AssetAdminDTO {
    id: string;
    deviceId: string;
    organizationId: string;

    name: string;
    sim: string;
    imei: string;
    deviceType: number;
    groupId: string;
}

export interface AssetInputDTO {
    enabled: boolean;
    number: number;
    activity: string;
    colour: string;
    indicator: string;
    invert: boolean;
}

export interface AssetAnalogInputDTO {
    enabled: boolean;
    number: number;
    activity: string;
    colour: string;
    indicator: string;
    high: string;
    low: string;
}

export interface AssetConfigurationDTO {
    spreader: string;
    inputs: AssetInputDTO[];
    analog: AssetAnalogInputDTO[];
    labelColour: string;
    textColour: string;
    usingBeacons: boolean;
    solidMaterials: string[];
    prewetMaterials: string[];
    antiiceMaterials: string[];
    icon: string;
    colour: string;
    cameraRotate: boolean;
    trackColour: string;
    rockCapacity: number;
    usingSpreaderPulse: boolean;
    spreaderPulseKg: number;
    oos: boolean;
    gradingWithSpeed: boolean;
    gradingWithSpeedKm: number;
    gradingWithSpeedTimeSec: number;

    mowingWithSpeed: boolean;
    mowingWithSpeedKm: number;
    mowingWithSpeedTimeSec: number;

    sweapingWithSpeed: boolean;
    sweapingWithSpeedKm: number;
    sweapingWithSpeedTimeSec: number;
}

export interface AssetTypeInputDTO {
    id: string;
    enabled: boolean;
    inputNumber: number;
    label: string;
    activeLabel: string;
    inactiveLabel: string;
    location: string;
    colour: string;
}
export interface AssetTypeDTO {
    id: string;
    name: string;
    count: number;
    inputs: AssetTypeInputDTO[];
}

export interface IdName {
    id: string;
    name: string;
}
export interface AssetDTO {
    id: IdName;
    type: IdName;
    secondary: number;
    description: string;
    assignedDevices: IdName[];
    config: AssetConfigurationDTO;
    assetType: string;
    odoOffset: number;
    hoursOffset: number;
    device: DeviceDTO;
    available511: boolean;
}

export class AssetService extends BaseService {
    public static TYPE_MOTORGRADER = 1;
    public static TYPE_MOWER = 5;

    public assetTypes: Map<string, AssetTypeDTO>;
    public assetList: Map<string, AssetDTO>;
    public loaded = false;

    public cache?: Promise<AssetDTO[]>;
    public dirty = false;

    private assetMap?: Map<string, AssetDTO>;
    private assetMapPromise?: Promise<Map<string, AssetDTO>>;
    private dirtyMap = true;

    constructor() {
        super();
        this.assetTypes = new Map<string, AssetTypeDTO>();
        this.assetList = new Map<string, AssetDTO>();
        this.assetMap = new Map<string, AssetDTO>();
    }

    public cachedAssetList(): Promise<AssetDTO[]> {
        // Check if the data for the given id is already cached
        if (this.cache && this.dirty == false) {
            return this.cache;
        } else {
            this.cache = this.fetchAssetList();
            this.dirty = false;
            return this.cache;
        }
    }

    public setAssetMapDirty() {
        this.dirtyMap = true;
    }

    public async getAssetMap(): Promise<Map<string, AssetDTO>> {
        if (this.assetMap && !this.dirtyMap) {
            return this.assetMap;
        }

        if (this.assetMapPromise) {
            return this.assetMapPromise;
        }

        this.assetMapPromise = this.loadAssetMap();

        try {
            return await this.assetMapPromise;
        } finally {
            this.assetMapPromise = undefined;
        }
    }

    private async loadAssetMap(): Promise<Map<string, AssetDTO>> {
        const url = `${this.url}/api/${this.orgid}/asset/edit`;

        const response = await fetch(url, {
            method: 'GET',
            headers: this.headers(),
        });

        const assets: AssetDTO[] = await response.json();

        this.assetMap = new Map(assets.map((asset) => [asset.id.id, asset]));

        this.dirtyMap = false;

        return this.assetMap;
    }

    private fetchAssetList(): Promise<AssetDTO[]> {
        const url = this.url + '/api/' + this.orgid + '/asset/edit';
        return fetch(url, {
            method: 'GET',
            headers: this.headers(),
        }).then((response) => {
            return response.json();
        });
    }

    /** Find out which input an activity is on.
     *
     * @param activity The name of the activity:
     *  * Moldboard
     * @param config  The configuration to look in
     * @returns The input number or undefined if not found.
     */
    public static findActivity(activity: string, config: AssetConfigurationDTO): number | undefined {
        for (const input of config.inputs) {
            if (input.activity == activity) {
                return input.number;
            }
        }
        /*
        for (let x = 0; x < config.inputs.length; x++) {
            if (config.inputs[x].activity == activity) {
                return config.inputs[x].number;
            }
        }
        */
        return undefined;
    }

    public static findAnalogActivity(activity: string, config: AssetConfigurationDTO): number | undefined {
        for (const analog of config.analog) {
            if (analog.activity == activity) {
                return analog.number;
            }
        }
        /*
        for (let x = 0; x < config.analog.length; x++) {
            if (config.analog[x].activity == activity) {
                return config.analog[x].number;
            }
        }
        */
        return undefined;
    }

    static findIndicatorForActivity(activity: string, config: AssetConfigurationDTO) {
        for (const input of config.inputs) {
            if (input.activity == activity) {
                return input.indicator;
            }
        }

        /*
        for (let x = 0; x < config.inputs.length; x++) {
            if (config.inputs[x].activity == activity) {
                return config.inputs[x].indicator;
            }
        }
        */
        return undefined;
    }

    /** Get the activity name for an asset and an input.
     *
     * @param assetId The id of the asset to check on.
     * @param input The input number to check
     * @param state The state of the input.
     * @returns The name of the activity for that input or undefined if no activity or it's not active.
     */
    public getActivityForInput(assetId: string, input: number, state: boolean): string | undefined {
        const asset = this.assetList.get(assetId);
        if (asset) {
            const config = asset.config;

            for (const inputConfig of config.inputs) {
                if (inputConfig.number == input) {
                    // If invert is false than the state is the same state as the input.
                    // If invert is true than the state is the opposite state of the input.
                    const activityState = inputConfig.invert == false ? state : !state;

                    if (inputConfig.enabled && activityState == true) {
                        return inputConfig.activity;
                    }
                }
            }
            /*
            for (let x = 0; x < config.inputs.length; x++) {
                const inputConfig = config.inputs[x];
                if (inputConfig.number == input) {
                    if (inputConfig.enabled && state == true) {
                        return inputConfig.activity;
                    }
                }
            }
            */
        }
        return undefined;
    }

    public async init() {
        return new Promise((resolve, _reject) => {
            const list = this.getAssetList();
            list.then((data) => {
                if (data.parsedBody) {
                    data.parsedBody?.forEach((item) => {
                        this.assetList.set(item.id.id, item);
                    });

                    const mapArray = Array.from(this.assetList.entries());
                    mapArray.sort((a, b) => a[1].id.name.localeCompare(b[1].id.name));
                    this.assetList = new Map(mapArray);
                    this.loaded = true;
                    console.log('Asset Service Loaded');
                    resolve('Loaded');
                }
            });
        });
    }

    public async cacheAssets() {
        const list = this.getAssetList();
        list.then((data) => {
            if (data.parsedBody) {
                data.parsedBody?.forEach((item) => {
                    this.assetList.set(item.id.id, item);
                });

                const mapArray = Array.from(this.assetList.entries());
                mapArray.sort((a, b) => a[1].id.name.localeCompare(b[1].id.name));
                this.assetList = new Map(mapArray);
                this.loaded = true;
                console.log('Asset Service Complete');
            }
        });
    }

    /** Create a cache of the asset types on startup.
     *
     */
    private cacheAssetTypes() {
        const list = this.getAssetTypeList();

        list.then((data) => {
            if (data.parsedBody) {
                for (let i = 0; i < data.parsedBody?.length; i++) {
                    const item = data.parsedBody[i];
                    item.inputs.sort((a, b) => (a.inputNumber < b.inputNumber ? -1 : 1));
                    this.assetTypes.set(item.id, item);
                }
            }
        });
    }

    /** Get information about a particular asset
     *  id: String The UUID of the asset we want to get the information for.
     */
    async getAsset(id: string) {
        const response = await this.get<AssetDTO>(this.url + '/api/' + this.orgid + '/asset/' + id);
        return response;
    }

    async getAssetList() {
        const response = await this.get<AssetDTO[]>(this.url + '/api/' + this.orgid + '/asset/edit');
        return response;
    }

    async updateAsset(asset: AssetDTO) {
        const response = await this.put<AssetDTO>(this.url + '/api/' + this.orgid + '/asset', asset);
        return response;
    }
    async create(asset: AssetDTO) {
        const response = await this.post<AssetDTO>(this.url + '/api/' + this.orgid + '/asset', asset);
        return response;
    }

    async createNewAssetType() {
        const response = await this.get<AssetTypeDTO>(this.url + '/api/' + this.orgid + '/assettype/new');
        return response;
    }

    async deleteAssetType(id: string | number) {
        const response = await this.delete(this.url + '/api/' + this.orgid + '/assettype/' + id, null);
        return response;
    }

    async saveAssetType(asset: AssetTypeDTO) {
        const response = await this.post<AssetTypeDTO>(this.url + '/api/' + this.orgid + '/assettype', asset);
        return response;
    }

    async getAssetTypeList() {
        const response = await this.get<AssetTypeDTO[]>(this.url + '/api/' + this.orgid + '/assettype');
        return response;
    }

    static getActivityStatus(activity: InputType, config: AssetConfigurationDTO, item: AssetTelemetry) {
        const digital = AssetService.getDigitalInput(config, activity);
        if (digital != undefined) {
            if (digital.enabled == true) {
                const value = LatestTelemetryService.getDigitalInput(item, digital.number);
                if (value != undefined) {
                    return value && !digital.invert;
                } else {
                    return undefined;
                }
            }
        } else if (digital == undefined) {
            const analog = AssetService.getAnalogInput(config, activity);
            if (analog != undefined) {
                const analogValue = LatestTelemetryService.getAnalogInput(item, analog.number);

                switch (activity) {
                    case InputType.SPREADER: {
                        if (analogValue != undefined) {
                            if (analogValue > Number(analog.low)) {
                                return true;
                            } else {
                                return false;
                            }
                        }
                    }
                }
            }
        }

        return undefined;
    }

    public static getDigitalInput(config: AssetConfigurationDTO, activity: InputType): AssetInputDTO | undefined {
        if (config.inputs == undefined) {
            return undefined;
        }

        for (const input of config.inputs) {
            if (input.activity == activity) {
                return input;
            }
        }
        /*
        for (let i = 0; i < config.inputs.length; i++) {
            if (config.inputs[i].activity == activity) {
                return config.inputs[i];
            }
        }
        */

        return undefined;
    }

    public static getAnalogInput(config: AssetConfigurationDTO, activity: InputType): AssetAnalogInputDTO | undefined {
        if (config.analog == undefined) {
            return undefined;
        }

        for (const analog of config.analog) {
            if (analog.activity == activity) {
                return analog;
            }
        }
        /*
        for (let i = 0; i < config.analog.length; i++) {
            if (config.analog[i].activity == activity) {
                return config.analog[i];
            }
        }
        */
        return undefined;
    }

    async deleteAsset(id: string) {
        this.assetList.delete(id);

        const response = await this.delete<AssetDTO>(this.url + '/api/' + this.orgid + '/asset', id);
        return response;
    }

    async getAll() {
        const response = await this.get<AssetAdminDTO[]>(this.url + '/api/admin/asset/list');
        return response;
    }

    public async save(asset: AssetAdminDTO) {
        if (asset.id != undefined && asset.id != '') {
            return await this.put<AssetAdminDTO>(this.url + '/api/admin/asset', asset);
        } else {
            return await this.post<AssetAdminDTO>(this.url + '/api/admin/asset', asset);
        }
    }

    async getDeviceInfo(id: string) {
        return await this.get<DeviceDTO>(this.url + '/api/' + this.orgid + '/asset/' + id + '/info');
    }

    public getAssetNameById(id: string): string | undefined {
        const asset = this.assetList.get(id);
        return asset ? asset.id.name : undefined;
    }

    /** Set the Out Of Service (OOS) status for an asset.
     *
     * @param assetId  The id of the asset to set the OOS status for.
     * @param oos The OOS status to set. True for OOS, false for in service.
     * @returns
     */
    public async setOOS(assetId: string, oos: boolean) {
        const response = await this.get(this.url + '/api/' + this.orgid + '/asset/' + assetId + '/oos' + (oos ? '/true' : '/false'));
        return response;
    }
}
